import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormatCurrency } from '../../../lib/CurrencyFormatter'
import { formatDate } from '../../../lib/Dates'
import { Sheet, Spend } from '../../../lib/Types'
import Headline from '../../Elements/Headline'
import Text from '../../Elements/Text'
import { useAccounting } from '../lib/Hooks/Accounting'
import { useExpenses, useUpdateSpend } from '../lib/Hooks/Spends'

interface Props {
  from: Date
  to: Date
  unpaid?: boolean
}

const Expenses: FC<Props> = ({ from, to, unpaid = false }) => {
  const { t } = useTranslation(['accounting'])
  const formatCurrency = useFormatCurrency()
  const { data: accounting } = useAccounting()
  const { data: expenses } = useExpenses(accounting!, {
    from,
    to,
    isExpense: true,
    nonPaidExpensesOnly: unpaid,
  })

  const sum = (): number => {
    return (
      expenses?.reduce((value: number, current: Spend) => {
        return value + current.amounts.at(-1)!.amount
      }, 0) || 0
    )
  }

  return (
    <>
      <Headline className='mb-4'>{t('accounting:expenses.heading')}</Headline>
      {expenses!.length > 0 && (
        <table className='w-full'>
          <thead>
            <tr>
              <th className='text-left'>{t('accounting:expenses.title')}</th>
              <th className='text-left'>{t('accounting:expenses.date')}</th>
              <th className='text-left'>{t('accounting:expenses.payer')}</th>
              <th className='text-left'>{t('accounting:expenses.amount')}</th>
              <th className='text-left'>{t('accounting:expenses.isPaid')}</th>
            </tr>
          </thead>
          <tbody>
            {expenses?.map((expense: Spend) => (
              <tr key={expense.id}>
                <td>{expense.title}</td>
                <td>
                  {formatDate(new Date(expense.accounting_date), 'dd/mm/yy')}
                </td>
                <td>{expense.expense_name!}</td>
                <td>{formatCurrency(expense.amounts.at(-1)!.amount)}</td>
                <td>
                  <ExpenseItem sheet={accounting!} spend={expense} />
                </td>
              </tr>
            ))}
            <tr>
              <td className='pt-2'>
                <strong>{t('accounting:expenses.total')}</strong>
              </td>
              <td></td>
              <td></td>
              <td>
                <strong>{formatCurrency(sum())}</strong>
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      )}
      {expenses!.length === 0 && (
        <Text size='base'>{t('accounting:expenses.empty')}</Text>
      )}
    </>
  )
}

interface ExpenseItemProps {
  sheet: Sheet
  spend: Spend
}
const ExpenseItem: FC<ExpenseItemProps> = ({ sheet, spend }) => {
  const [isChecked, toggleChecked] = useState(spend.expense_paid)
  const { mutateAsync: updateSpend } = useUpdateSpend(sheet)

  return (
    <input
      checked={isChecked}
      type='checkbox'
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        toggleChecked(event.target.checked)
        const newSpend = structuredClone(spend)
        newSpend.expense_paid = event.target.checked
        updateSpend(newSpend)
      }}
    />
  )
}

export default Expenses
